import { JSONUtils } from '../../utility/json-utils.js';
import { MapUtils } from '../../utility/map-utils.js';
import { cloneListOfCloneable, isSameData, isSameList } from '../dto/data-model-utils.js';
import {
  DataModel, DataModelBuilder, SerializationContext, SerializedData,
} from '../dto/data-model.js';
import { OrganizerProfile } from './organizer-profile.js';
import { EventInfo } from './event-info.js';
import { VersionRange } from '../core/version-range.js';

export class OrganizerPageInfo extends DataModel {
  static readonly TYPE_ID: string = '146c89d7fa5747ef884407eaf747c94a';
  static readonly SCHEMA_VERSION: number = 2;

  private constructor(
    public readonly organizer: OrganizerProfile,
    public readonly events: EventInfo[],
    public readonly metaPixelId: string | null,
    public readonly tikTokPixelId: string | null,
    public readonly googleTagManagerId: string | null,
  ) {
    super();
  }

  get $typeId(): string {
    return OrganizerPageInfo.TYPE_ID;
  }

  get $schemaVersion(): VersionRange {
    return VersionRange.between(1, OrganizerPageInfo.SCHEMA_VERSION);
  }

  static create(args: {
    organizer: OrganizerProfile,
    events: EventInfo[],
    metaPixelId: string | null,
    tikTokPixelId: string | null,
    googleTagManagerId: string | null,
  }): OrganizerPageInfo {
    return new OrganizerPageInfo(
        args.organizer,
        args.events,
        args.metaPixelId,
        args.tikTokPixelId,
        args.googleTagManagerId,
    );
  }

  static fromMap(context: SerializationContext, map: SerializedData): OrganizerPageInfo {
    return OrganizerPageInfo.create({
      organizer: context.fromData(JSONUtils.decodeRecord(MapUtils.get<unknown>(map, 'organizer'))),
      events: MapUtils.getList<EventInfo>(
          map,
          'events',
          (data) => EventInfo.fromMap(context, JSONUtils.decodeRecord(data)),
      ),
      metaPixelId: MapUtils.getStringOrNull(map, 'metaPixelId'),
      tikTokPixelId: MapUtils.getStringOrNull(map, 'tikTokPixelId'),
      googleTagManagerId: MapUtils.getStringOrNull(map, 'googleTagManagerId'),
    });
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      organizer: context.toData(this.organizer),
      events: this.events.map((event) => event.toMap(context)),
      metaPixelId: this.metaPixelId,
      tikTokPixelId: this.tikTokPixelId,
      googleTagManagerId: this.googleTagManagerId,
    };
  }

  clone(): this {
    return OrganizerPageInfo.create({
      organizer: this.organizer.clone(),
      events: cloneListOfCloneable(this.events),
      metaPixelId: this.metaPixelId,
      tikTokPixelId: this.tikTokPixelId,
      googleTagManagerId: this.googleTagManagerId,
    }) as this;
  }

  isSame(other: this): boolean {
    if(other === this) {
      return true;
    }

    return isSameData(other.organizer, this.organizer) &&
        isSameList(other.events, this.events, (a, b) => isSameData(a, b)) &&
        other.metaPixelId === this.metaPixelId &&
        other.tikTokPixelId === this.tikTokPixelId &&
        other.googleTagManagerId === this.googleTagManagerId;
  }
}

export class OrganizerPageInfoBuilder extends DataModelBuilder<OrganizerPageInfo> {
  build(context: SerializationContext, map: SerializedData): OrganizerPageInfo {
    return OrganizerPageInfo.fromMap(context, map);
  }

  migrateToCurrentSchemaVersion(context: SerializationContext, map: SerializedData): SerializedData | null {
    const versionRange = MapUtils.getVersionRange(map);
    if(versionRange.contains(OrganizerPageInfo.SCHEMA_VERSION)) {
      return map;
    }
    if(versionRange.isGreaterThan(OrganizerPageInfo.SCHEMA_VERSION)) {
      return null; // This data comes from the FUTURE. We have no idea how to migrate it.
    }

    let version = versionRange.to;

    if(version < 2) {version = 2;}

    map['$schema_version'] = VersionRange.encodedExactly(version);
    return map;
  }
}
